import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { ListSkeleton } from "@/components/common/states";
import { ThreadList } from "@/components/mail/thread-list";
import { q } from "@/data/queries";

/** The providers whose mailboxes Aux0 reads, in catalog order. */
const MAIL_PROVIDERS = ["gmail", "outlook", "icloud", "fastmail", "zoho", "imap"];

/**
 * Mail is a two-pane layout: the thread list stays mounted while the thread
 * changes, so switching threads does not refetch or re-scroll the list. On a
 * narrow screen the list fills the width and the thread is its own page.
 */
export const Route = createFileRoute("/p/$project/mail")({
  loader: async ({ context }) => {
    const [accounts] = await Promise.all([
      context.queryClient.ensureQueryData(q.accounts({ limit: 200 })),
      context.queryClient.ensureQueryData(q.mailThreads()),
    ]);
    const mail = accounts.items.filter((account) =>
      MAIL_PROVIDERS.includes(account.provider),
    );
    // Folders are per account and the account list is data, so the number of
    // reads is not knowable at render time — which rules out a hook per
    // account. The loader gathers them and the component takes them as one
    // array.
    const folders = (
      await Promise.all(
        mail.map((account) =>
          context.queryClient.ensureQueryData(q.mailFolders(account.id)),
        ),
      )
    ).flat();
    return { accountIds: mail.map((account) => account.id), folders };
  },
  pendingComponent: () => (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <div className="w-full border-r lg:w-[400px]">
        <ListSkeleton />
      </div>
    </div>
  ),
  component: MailLayout,
});

function MailLayout() {
  const { project } = Route.useParams();
  const { accountIds, folders } = Route.useLoaderData();
  const { data: accounts } = useSuspenseQuery(q.accounts({ limit: 200 }));
  const { data: threads } = useSuspenseQuery(q.mailThreads());
  const mail = accounts.items.filter((account) => accountIds.includes(account.id));

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <ThreadList
        threads={threads.items}
        accounts={mail}
        folders={folders}
        project={project}
      />
      <div className="hidden min-h-0 flex-1 lg:flex">
        <Outlet />
      </div>
    </div>
  );
}
